import { useState } from "react";
import {
  FileTextIcon,
  Maximize2Icon,
  MinusIcon,
  Minimize2Icon,
  PlusIcon,
  XIcon,
} from "lucide-react";

import { CandidacyStafferCallsList } from "./CandidacyStafferCallsList";
import { KickoffAiCallActions } from "./KickoffAiCallActions";
import { PastApplicationAnswersDropdown } from "./PastApplicationAnswersDropdown";
import { RecordsSection } from "@/components/user-sheet/records/RecordsSection";
import { ResumeCard } from "@/components/user-sheet/records/ResumeCard";
import { ResumeListDropdown } from "@/components/user-sheet/records/ResumeListDropdown";
import { ScreeningAnswers } from "./ScreeningAnswers";
import { UploadResumeDialog } from "@/components/user-sheet/records/UploadResumeDialog";
import { VoiceAiCallsList } from "@/components/user-sheet/records/VoiceAiCallsList";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { userSheetViewStore } from "@/stores/userSheetView";
import { withRequiredFields } from "@/components/user-sheet/withRequiredFields";

import {
  Center,
  Column,
  DocumentPreview,
  EmptyState,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetOtherCandidacies } from "@/hooks/queries/useGetOtherCandidacies";
import { STEVIE_TAB_JOB_ID } from "@/lib/constants";
import { pastApplications as pastApplicationsLib } from "@/lib/pastApplications";
import { sort } from "@/lib/sort";
import { cn } from "@/lib/utils";
import { ResumeFileType } from "@/types";

const DEFAULT_RESUME_ZOOM = 1;
const DEFAULT_SPLIT_RESUME_ZOOM = 1.35;
const MAX_RESUME_ZOOM = 2;
const MIN_RESUME_ZOOM = 0.7;
const RESUME_ZOOM_STEP = 0.15;

// Candidacy-scoped records tab: adds application answers and pins the
// current job's calls above calls from the candidate's other jobs.
export const CandidacyRecordsTab = () => {
  /* Context */
  const { candidacyId, contentPaddingClassName, isReadOnly, jobId, workerId } =
    useUserSheet();
  const isResumesCollapsed = userSheetViewStore((s) => s.isResumesCollapsed);
  const setIsResumesCollapsed = userSheetViewStore(
    (s) => s.setIsResumesCollapsed,
  );
  const userResumeZoom = userSheetViewStore((s) => s.resumeZoom);
  const setResumeZoom = userSheetViewStore((s) => s.setResumeZoom);
  const isSplitView = userSheetViewStore((s) => s.splitTabValues.length > 0);

  /* Database CRUD Operations */
  // Read
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });
  const callHistoryQuery = useGetCallHistory(workerId);
  const candidacyInfoQuery = useGetCandidacyInfo({
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    candidacyId: candidacyId || "",
  });
  const otherCandidaciesQuery = useGetOtherCandidacies({
    candidacyId,
    talentId: workerId,
  });

  /* React Hooks */
  const [inlineResume, setInlineResume] = useState<ResumeFileType>();
  const [primaryResumeOverride, setPrimaryResumeOverride] = useState<number>();

  /* Helpers */
  // Until the staffer zooms, follow the layout: split panes are narrow, so
  // their default renders the resume larger. A chosen zoom sticks after that.
  const resumeZoom =
    userResumeZoom ??
    (isSplitView ? DEFAULT_SPLIT_RESUME_ZOOM : DEFAULT_RESUME_ZOOM);
  const profile = userApi.data;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const candidateFirstName = profile?.profile.firstName || "Candidate";
  const primaryResumeId =
    primaryResumeOverride ?? profile?.primaryResumeId ?? null;
  const resumeFiles = profile
    ? sort.sortResumeFilesDesc(
        withRequiredFields(profile.resumeFiles, ["id", "title"]),
      )
    : [];
  const featuredResume =
    resumeFiles.find((resumeFile) => resumeFile.id === primaryResumeId) ??
    resumeFiles.at(0);
  const screeningAnswers = candidacyInfoQuery.data?.screeningAnswers ?? null;
  const answeredScreeningCount = (
    screeningAnswers?.screeningAnswers ?? []
  ).filter(
    (screeningAnswer) => (screeningAnswer.answer ?? "").trim() !== "",
  ).length;
  const pastApplications = pastApplicationsLib.fromOtherCandidacies(
    otherCandidaciesQuery.data,
  );
  const stafferCalls = callHistoryQuery.data?.stafferCalls ?? [];
  const voiceAiCalls = callHistoryQuery.data?.aiCalls ?? [];

  /* Handlers */
  const handleOpenInlineResume = (resumeFile: ResumeFileType) => {
    setInlineResume(resumeFile);
  };

  /* Render */
  const renderApplicationAnswersContent = () => {
    if (candidacyInfoQuery.isPending) {
      return <LoadingIndicator variant="medium" />;
    }
    if (candidacyInfoQuery.isError) {
      return <ErrorText errorMessage="Error retrieving application answers." />;
    }

    return (
      <ScreeningAnswers
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        candidacyId={candidacyId || ""}
        screeningAnswers={screeningAnswers}
      />
    );
  };

  const renderApplicationAnswers = () => {
    return (
      <RecordsSection
        count={answeredScreeningCount}
        countControl={
          pastApplications.length ? (
            <PastApplicationAnswersDropdown
              pastApplications={pastApplications}
            />
          ) : undefined
        }
        isFirst
        title="Application Answers"
      >
        <Column
          className={cn(
            "rounded-xl border border-slate-200 bg-slate-50/50 p-5",
            answeredScreeningCount === 0 && "min-h-0 flex-1 justify-center",
          )}
        >
          {renderApplicationAnswersContent()}
        </Column>
      </RecordsSection>
    );
  };

  const renderResumesContent = () => {
    if (userApi.isPending) {
      return <LoadingIndicator variant="medium" />;
    }
    if (!resumeFiles.length) {
      return (
        <Column className="h-64 w-full min-w-[180px] max-w-60 rounded-xl border border-slate-200 bg-slate-50/50 p-5">
          <EmptyState
            className="h-full py-0"
            description="Upload a resume to this candidate's profile."
            descriptionClassName="text-center text-xs"
            headerClassName="text-sm"
            Icon={FileTextIcon}
            isIconHidden
            SecondaryButtonComponent={
              isReadOnly
                ? undefined
                : () => (
                    <UploadResumeDialog
                      buttonClassName="rounded-md"
                      onUploaded={() => {
                        void userApi.refetch();
                      }}
                      userId={workerId}
                      variant="secondary"
                    />
                  )
            }
            title="No resumes"
          />
        </Column>
      );
    }

    return (
      <Row className="justify-end">
        {featuredResume ? (
          <ResumeCard
            isPrimary={featuredResume.id === primaryResumeId}
            onOpenInline={handleOpenInlineResume}
            onResumeUpdated={() => {
              void userApi.refetch();
            }}
            primaryResumeId={primaryResumeId}
            resumeFile={featuredResume}
            userId={workerId}
          />
        ) : null}
      </Row>
    );
  };

  const renderResumesCount = () => {
    if (resumeFiles.length <= 1) return undefined;

    return (
      <ResumeListDropdown
        onPrimaryResumeChanged={setPrimaryResumeOverride}
        onResumeUpdated={() => {
          void userApi.refetch();
        }}
        primaryResumeId={primaryResumeId}
        resumeFiles={resumeFiles}
        totalCount={resumeFiles.length}
        userId={workerId}
      />
    );
  };

  const renderResumesSection = () => {
    if (isResumesCollapsed) {
      return (
        <Column className="w-12 items-end justify-self-end">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="relative h-12 w-12 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                  onClick={() => {
                    setIsResumesCollapsed(false);
                  }}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <FileTextIcon className="h-4 w-4 text-primary-900" />
                  <Center className="absolute -right-1 -top-1 h-5 min-w-5 rounded-full bg-slate-600 px-1 text-[10px] font-semibold text-white">
                    {resumeFiles.length}
                  </Center>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <Row className="items-center gap-1.5">
                  <Maximize2Icon className="h-3 w-3" />
                  <Text className="text-xs font-normal">Expand resumes</Text>
                </Row>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Column>
      );
    }

    return (
      <Column className="group/resumes w-full min-w-[180px] max-w-60 justify-self-end">
        <RecordsSection
          count={resumeFiles.length}
          countControl={renderResumesCount()}
          isFirst
          title="Resumes"
          titleActions={
            <Row className="items-center gap-1">
              <Button
                className="h-6 w-6 shrink-0 text-slate-400 opacity-0 transition-opacity hover:text-slate-600 group-hover/resumes:opacity-100"
                onClick={() => {
                  setIsResumesCollapsed(true);
                }}
                size="icon"
                title="Minimize resumes"
                type="button"
                variant="ghost"
              >
                <Minimize2Icon className="h-3.5 w-3.5" />
              </Button>
              {isReadOnly ? null : (
                <UploadResumeDialog
                  isIconOnly
                  onUploaded={() => {
                    void userApi.refetch();
                  }}
                  userId={workerId}
                />
              )}
            </Row>
          }
        >
          {renderResumesContent()}
        </RecordsSection>
      </Column>
    );
  };

  const renderInlineResume = () => {
    if (!inlineResume) return null;

    return (
      <Column
        className={cn(
          "sticky top-11 h-[calc(100vh-14rem)] gap-4 py-7",
          contentPaddingClassName,
        )}
      >
        <Row className="items-center justify-between gap-3">
          <Text className="truncate text-base font-medium text-slate-900">
            {inlineResume.title || "Resume"}
          </Text>
          <Button
            className="h-8 w-8 shrink-0 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            onClick={() => {
              setInlineResume(undefined);
            }}
            size="icon"
            title="Close inline resume"
            type="button"
            variant="ghost"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </Row>
        <Column className="relative isolate min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <Row className="absolute right-3 top-3 z-10 items-center rounded-md border border-slate-200 bg-white/90 p-0.5 shadow-sm backdrop-blur-sm">
            <Button
              className="h-7 w-7 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              disabled={resumeZoom <= MIN_RESUME_ZOOM}
              onClick={() => {
                setResumeZoom(
                  Math.max(MIN_RESUME_ZOOM, resumeZoom - RESUME_ZOOM_STEP),
                );
              }}
              size="icon"
              title="Zoom out"
              type="button"
              variant="ghost"
            >
              <MinusIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
              className="h-7 w-7 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              disabled={resumeZoom >= MAX_RESUME_ZOOM}
              onClick={() => {
                setResumeZoom(
                  Math.min(MAX_RESUME_ZOOM, resumeZoom + RESUME_ZOOM_STEP),
                );
              }}
              size="icon"
              title="Zoom in"
              type="button"
              variant="ghost"
            >
              <PlusIcon className="h-3.5 w-3.5" />
            </Button>
          </Row>
          <Column className="min-h-0 flex-1 overflow-auto p-4">
            <DocumentPreview
              centered={resumeZoom <= DEFAULT_RESUME_ZOOM}
              href={inlineResume.url ?? ""}
              pageGap={12}
              pageWidthScale={resumeZoom}
            />
          </Column>
        </Column>
      </Column>
    );
  };

  // Jobs piloting the Stevie tab own their AI calls there instead, kickoff
  // buttons included.
  const renderAiCallsSection = () => {
    if (jobId === STEVIE_TAB_JOB_ID) return null;

    return (
      <RecordsSection
        collapsible
        count={voiceAiCalls.length}
        title="AI Calls"
        titleActions={
          candidacyId && !isReadOnly ? (
            <KickoffAiCallActions
              candidacyId={candidacyId}
              workerId={workerId}
            />
          ) : null
        }
      >
        <VoiceAiCallsList
          calls={voiceAiCalls}
          candidateFirstName={candidateFirstName}
          currentCandidacyId={candidacyId}
          isError={callHistoryQuery.isError}
          isPending={callHistoryQuery.isPending}
        />
      </RecordsSection>
    );
  };

  const render = () => {
    if (inlineResume) return renderInlineResume();

    return (
      <Column className={cn("py-7", contentPaddingClassName)}>
        <div
          className={cn(
            "grid gap-8",
            isResumesCollapsed
              ? "grid-cols-[minmax(0,1fr)_3rem]"
              : "grid-cols-[minmax(0,5fr)_minmax(180px,1fr)]",
          )}
        >
          {renderApplicationAnswers()}
          {renderResumesSection()}
        </div>
        {renderAiCallsSection()}
        <RecordsSection
          collapsible
          count={stafferCalls.length}
          title="Staffer Calls"
        >
          <CandidacyStafferCallsList
            calls={stafferCalls}
            candidateFirstName={candidateFirstName}
            isError={callHistoryQuery.isError}
            isPending={callHistoryQuery.isPending}
          />
        </RecordsSection>
      </Column>
    );
  };

  return render();
};
